import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { HardwireLogo } from "../components/HardwireMark";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep polling */ }
      tries += 1;
      if (tries >= 8) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-5">
      <div className="w-full max-w-md text-center">
        <div className="mb-6 flex justify-center"><HardwireLogo size={44} /></div>
        <div className="rounded-3xl border border-slate-200 bg-white p-10 shadow-xl" data-testid="payment-success">
          {state === "checking" && (<>
            <Loader2 size={48} className="mx-auto animate-spin text-[#1286F2]" />
            <h1 className="mt-4 text-2xl font-extrabold text-slate-900">Confirming your payment…</h1>
            <p className="mt-2 text-slate-500">One moment while we verify with Stripe.</p>
          </>)}
          {state === "paid" && (<>
            <CheckCircle2 size={56} className="mx-auto text-emerald-500" data-testid="payment-paid" />
            <h1 className="mt-4 text-2xl font-extrabold text-slate-900">Payment confirmed — thank you!</h1>
            <p className="mt-2 text-slate-500">Stripe has confirmed the payment. Contact the office about any next steps for your project.</p>
          </>)}
          {state === "timeout" && (<>
            <Loader2 size={48} className="mx-auto text-amber-500" />
            <h1 className="mt-4 text-2xl font-extrabold text-slate-900">Still processing</h1>
            <p className="mt-2 text-slate-500">Your payment has not yet been confirmed. Check your portal later or contact the office before trying another payment.</p>
          </>)}
          {state === "error" && (<>
            <AlertCircle size={48} className="mx-auto text-rose-500" />
            <h1 className="mt-4 text-2xl font-extrabold text-slate-900">We couldn't confirm this payment</h1>
            <p className="mt-2 text-slate-500">Please contact us and we'll help right away.</p>
          </>)}
          <button onClick={() => navigate("/")} className="mt-6 rounded-xl bg-[#1286F2] px-6 py-3 font-bold text-white hover:bg-[#0A84FF]" data-testid="success-home-btn">Back to Home</button>
        </div>
      </div>
    </div>
  );
}
